import { USAGE_ACTION, isCreationRecord } from '../constants/sample.constants';

/** ソート・残量計算に使う最小フィールド（API の UsageRecord もそのまま渡せる） */
export interface UsageRecordForSort {
  id?: number;
  usageDate?: string;
  action?: string;
  purpose?: string | null;
  remainingQuantity?: number | null;
  quantity?: number | null;
  createdAt?: string;
  notes?: string | null;
  recordedBy?: string | null;
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  };
}

export interface EnrichedUsageRecord extends UsageRecordForSort {
  calculatedRemaining: number;
  isCreationRecord: boolean;
}

/** サンプル詳細の使用履歴プレビュー件数（直近 N 件） */
export const SAMPLE_DETAIL_RECENT_USAGE_LIMIT = 5;

/**
 * サンプル詳細向け: 直近の使用履歴のみ返す（新しい順）。
 * 全件ページ（/usage-history）は sortAndEnrichUsageRecords の古い順のまま利用する。
 */
export function getRecentUsageRecordsForDetail(
  records: UsageRecordForSort[],
  initialQuantityFallback: number | null | undefined,
  limit: number = SAMPLE_DETAIL_RECENT_USAGE_LIMIT
): { recentRecords: EnrichedUsageRecord[]; totalCount: number } {
  const enriched = sortAndEnrichUsageRecords(records, initialQuantityFallback);
  const totalCount = enriched.length;
  if (totalCount === 0) {
    return { recentRecords: [], totalCount: 0 };
  }
  const recentRecords =
    totalCount <= limit ? [...enriched].reverse() : enriched.slice(-limit).reverse();
  return { recentRecords, totalCount };
}

/**
 * 使用記録を時系列（古い順）でソートし、作成記録を同日内で先頭にしつつ、
 * 各レコードに calculatedRemaining と isCreationRecord を付与する。
 */
export function sortAndEnrichUsageRecords(
  records: UsageRecordForSort[],
  initialQuantityFallback: number | null | undefined
): EnrichedUsageRecord[] {
  const sorted = [...records].sort((a, b) => {
    const dateA = a.usageDate ? new Date(a.usageDate).getTime() : 0;
    const dateB = b.usageDate ? new Date(b.usageDate).getTime() : 0;
    if (dateA !== dateB) return dateA - dateB;
    const aIsCreation = isCreationRecord(a);
    const bIsCreation = isCreationRecord(b);
    if (aIsCreation && !bIsCreation) return -1;
    if (!aIsCreation && bIsCreation) return 1;
    const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return createdA - createdB;
  });

  const creationRecord = sorted.find(isCreationRecord);
  const initialQuantity = creationRecord?.remainingQuantity ?? initialQuantityFallback ?? 0;

  let currentRemaining = initialQuantity;

  return sorted.map((record) => {
    const isCreation = isCreationRecord(record);
    if (isCreation) {
      currentRemaining = record.remainingQuantity ?? initialQuantity;
    } else {
      if (record.remainingQuantity !== null && record.remainingQuantity !== undefined) {
        currentRemaining = record.remainingQuantity;
      } else {
        const action = record.action || USAGE_ACTION.USE;
        if (action === USAGE_ACTION.USE || action === USAGE_ACTION.DISPOSE) {
          const usedQuantity = record.quantity ?? 0;
          currentRemaining = Math.max(0, currentRemaining - usedQuantity);
        }
      }
    }
    return {
      ...record,
      calculatedRemaining: currentRemaining,
      isCreationRecord: isCreation,
    };
  });
}

/** 使用記録を追加できる状態か（廃棄済み・残量0を除外。残量 null は数量未管理として許可） */
export function canSampleAcceptUsageRecord(sample: {
  disposalDate?: string | null;
  remainingQuantity?: number | null;
}): boolean {
  if (sample.disposalDate) return false;
  return sample.remainingQuantity == null || sample.remainingQuantity > 0;
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** 使用不可の理由文（追加可能なときは null） */
export function sampleUsageBlockReason(
  sample: {
    disposalDate?: string | null;
    remainingQuantity?: number | null;
  },
  t: TranslateFn
): string | null {
  if (canSampleAcceptUsageRecord(sample)) return null;
  if (sample.disposalDate) {
    return t('sample.cannot_add_usage_disposed', {
      defaultValue: t('sample.cannot_add_usage'),
    });
  }
  return t('sample.cannot_add_usage');
}

/** 一覧チップ用の短いラベル（追加可能なときは null） */
export function sampleUsageBlockShortLabel(
  sample: {
    disposalDate?: string | null;
    remainingQuantity?: number | null;
  },
  t: TranslateFn
): string | null {
  if (canSampleAcceptUsageRecord(sample)) return null;
  if (sample.disposalDate) {
    return t('sample.usage_blocked_disposed_short', { defaultValue: '廃棄済' });
  }
  return t('sample.usage_blocked_depleted_short', { defaultValue: '残0' });
}

/** 一覧カード／行の視覚トーン（廃棄・切れをスキャンしやすくする） */
export type SampleListRowTone = 'disposed' | 'depleted' | 'normal';

/** 残数量バッジの Bootstrap `bg`（null 残量は情報なしとして secondary） */
export function sampleRemainingQuantityBadgeBg(sample: {
  remainingQuantity?: number | null;
  initialQuantity?: number | null;
}): 'danger' | 'warning' | 'success' | 'secondary' {
  if (sample.remainingQuantity == null) return 'secondary';
  if (sample.remainingQuantity === 0) return 'danger';
  const initial = sample.initialQuantity ?? 0;
  if (initial > 0 && sample.remainingQuantity < initial / 2) return 'warning';
  return 'success';
}

export function getSampleListRowTone(sample: {
  disposalDate?: string | null;
  remainingQuantity?: number | null;
}): SampleListRowTone {
  if (sample.disposalDate) return 'disposed';
  if (sample.remainingQuantity === 0) return 'depleted';
  return 'normal';
}

/** DataTables `createdRow` / CSS 用。normal は空文字 */
export function sampleListRowToneClass(tone: SampleListRowTone): string {
  if (tone === 'normal') return '';
  return `sample-row--${tone}`;
}
